// src/services/demoApi/damApi.ts
// Демо-API корпоративного DAM-хранилища. Поиск выполняется по мок-каталогу
// с имитацией сетевой задержки (см. спеку FR-1).
import { DAM_FILES, DAM_FOLDERS, type DamFile, type DamFolder } from 'mocks/dam';
import { DAM_CONTENTS, type DamFileContent } from 'mocks/damContents';
import { demoDelay, readDemoStore, writeDemoStore } from './demoStorage';

export interface DamSearchFilters {
  query?: string;
  docType?: string;
  department?: string;
  ext?: string;
}

const CREATED_KEY = 'demo_dam_created_files';

interface StoredCreatedFile {
  file: DamFile;
  content: DamFileContent;
}

/** Созданные агентом файлы (localStorage), синхронно */
export function listCreatedFilesSync(folderId?: string): StoredCreatedFile[] {
  const all = readDemoStore<StoredCreatedFile[]>(CREATED_KEY, []);
  return folderId ? all.filter((s) => s.file.folderId === folderId) : all;
}

/** Имя с суффиксом «(2)» при дублях (EC-4, EC-11) */
export function uniqueFileName(desired: string, existing: string[]): string {
  const base = desired.trim();
  if (!existing.includes(base)) return base;
  let n = 2;
  while (existing.includes(`${base} (${n})`)) n += 1;
  return `${base} (${n})`;
}

export interface CreateDamFileInput {
  folderId: string;
  name: string;
  ext: DamFile['ext'];
  content: DamFileContent;
}

/** Создать файл от имени ИИ-агента (FR-3.2). Синхронно — вызывается из эффекта чата */
export function createDamFile(input: CreateDamFileInput): DamFile {
  const existingNames = [
    ...DAM_FILES.filter((f) => f.folderId === input.folderId).map((f) => f.name),
    ...listCreatedFilesSync(input.folderId).map((s) => s.file.name),
  ];
  const file: DamFile = {
    id: `created-${Date.now()}`,
    name: uniqueFileName(input.name, existingNames),
    ext: input.ext,
    folderId: input.folderId,
    sizeKb: Math.max(8, Math.round(JSON.stringify(input.content).length / 512)),
    author: 'ИИ-ассистент',
    department: 'ИИ-ассистент',
    docType: 'Сгенерированный',
    updatedAt: new Date().toISOString().slice(0, 10),
  };
  writeDemoStore(CREATED_KEY, [...listCreatedFilesSync(), { file, content: input.content }]);
  return file;
}

/** Найти созданный агентом файл по имени из маркера: точное имя или имя
 *  с суффиксом «(n)» от uniqueFileName. Берём самый свежий (id содержит timestamp). */
export function findCreatedFileSync(name: string, ext: DamFile['ext']): DamFile | null {
  const base = name.trim();
  const candidates = listCreatedFilesSync()
    .map((s) => s.file)
    .filter(
      (f) =>
        f.ext === ext &&
        (f.name === base || new RegExp(`^${escapeRegExp(base)} \\(\\d+\\)$`).test(f.name))
    );
  if (candidates.length === 0) return null;
  return candidates.sort((a, b) => (a.id < b.id ? 1 : -1))[0];
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Удалить сгенерированный агентом файл (FR-7) */
export async function deleteCreatedFile(id: string): Promise<void> {
  await demoDelay(250);
  const all = listCreatedFilesSync();
  writeDemoStore(
    CREATED_KEY,
    all.filter((s) => s.file.id !== id)
  );
}

/** Содержимое файла: статика → созданные. Загруженные добавит Task 3 */
export async function getDamFileContent(fileId: string): Promise<DamFileContent | null> {
  if (DAM_CONTENTS[fileId]) return DAM_CONTENTS[fileId];
  const created = listCreatedFilesSync().find((s) => s.file.id === fileId);
  if (created) return created.content;
  if (fileId.startsWith('upload-')) {
    const { listUploadedFiles } = await import('./damUploads');
    const uploaded = (await listUploadedFiles()).find((u) => u.meta.id === fileId);
    if (uploaded?.extractedText) {
      return {
        kind: 'text',
        sections: [{ heading: uploaded.meta.name, body: uploaded.extractedText }],
      };
    }
  }
  return null;
}

async function listUploadedMetas(folderId?: string): Promise<DamFile[]> {
  const { listUploadedFiles } = await import('./damUploads');
  return (await listUploadedFiles(folderId)).map((u) => u.meta);
}

/** Чистая функция фильтрации — отдельно от задержки, чтобы покрыть тестами */
export function filterDamFiles(files: DamFile[], filters: DamSearchFilters): DamFile[] {
  const query = filters.query?.trim().toLowerCase();
  return files.filter((file) => {
    if (query && !file.name.toLowerCase().includes(query)) return false;
    if (filters.docType && file.docType !== filters.docType) return false;
    if (filters.department && file.department !== filters.department) return false;
    if (filters.ext && file.ext !== filters.ext) return false;
    return true;
  });
}

export async function searchDamFiles(filters: DamSearchFilters): Promise<DamFile[]> {
  await demoDelay(450);
  const uploaded = await listUploadedMetas();
  const catalog = [...DAM_FILES, ...listCreatedFilesSync().map((s) => s.file), ...uploaded];
  return filterDamFiles(catalog, filters);
}

export async function listDamFolders(): Promise<Array<DamFolder & { fileCount: number }>> {
  await demoDelay(250);
  const uploaded = await listUploadedMetas();
  const catalog = [...DAM_FILES, ...listCreatedFilesSync().map((s) => s.file), ...uploaded];
  return DAM_FOLDERS.map((folder) => ({
    ...folder,
    fileCount: catalog.filter((file) => file.folderId === folder.id).length,
  }));
}

export async function listDamFolderFiles(folderId: string): Promise<DamFile[]> {
  await demoDelay(300);
  const uploaded = await listUploadedMetas(folderId);
  const catalog = [
    ...DAM_FILES.filter((f) => f.folderId === folderId),
    ...listCreatedFilesSync(folderId).map((s) => s.file),
    ...uploaded,
  ];
  return catalog;
}
